import React, { createContext, useContext, useEffect, useState } from 'react';
import { supabase } from '../lib/supabase';
import { cacheUserTimezone } from '../utils/timezone';

const AuthContext = createContext(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
}

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null)
  const [userProfile, setUserProfile] = useState(null)
  const [loading, setLoading] = useState(true)

  // Isolated async operations - never called from auth callbacks
  const profileOperations = {
    async load(userId) {
      if (!userId) return
      try {
        const { data, error } = await supabase?.from('user_profiles')?.select('*')?.eq('id', userId)?.single()
        if (!error) setUserProfile(data)
      } catch (error) {
        console.error('Profile load error:', error)
      }
    },

    clear() {
      setUserProfile(null)
    }
  }

  // Clear corrupted session from localStorage
  const clearCorruptedSession = async () => {
    try {
      // Clear Supabase session
      await supabase?.auth?.signOut({ scope: 'local' })
      // Clear any remaining localStorage items
      const keys = Object.keys(localStorage)
      keys?.forEach(key => {
        if (key?.startsWith('sb-')) {
          localStorage.removeItem(key)
        }
      })
    } catch (error) {
      console.error('Error clearing session:', error)
    }
  }

  // Auth state handlers - PROTECTED from async modification
  const authStateHandlers = {
    // This handler MUST remain synchronous - Supabase requirement
    onChange: (event, session) => {
      setUser(session?.user ?? null)
      setLoading(false)
      
      if (session?.user) {
        profileOperations?.load(session?.user?.id) // Fire-and-forget
      } else {
        profileOperations?.clear()
      }
    }
  }

  useEffect(() => {
    // Initial session check with error handling
    supabase?.auth?.getSession()?.then(({ data: { session }, error }) => {
      // Check for refresh token errors
      if (error && (error?.message?.includes('refresh') || error?.message?.includes('Refresh Token'))) {
        console.warn('Invalid refresh token detected, clearing session:', error?.message)
        clearCorruptedSession()?.then(() => {
          setUser(null)
          setLoading(false)
          profileOperations?.clear()
        })
        return
      }
      
      authStateHandlers?.onChange(null, session)
    })?.catch((error) => {
      // Catch any unexpected errors during session retrieval
      console.error('Session retrieval error:', error)
      if (error?.message?.includes('refresh') || error?.message?.includes('Refresh Token')) {
        clearCorruptedSession()?.then(() => {
          setUser(null)
          setLoading(false)
          profileOperations?.clear()
        })
      } else {
        setLoading(false)
      }
    })

    // CRITICAL: This must remain synchronous
    const { data: { subscription } } = supabase?.auth?.onAuthStateChange(
      (event, session) => {
        // Handle TOKEN_REFRESHED event errors
        if (event === 'TOKEN_REFRESHED' && !session) {
          console.warn('Token refresh failed, clearing session')
          clearCorruptedSession()?.then(() => {
            setUser(null)
            profileOperations?.clear()
          })
          return
        }
        
        authStateHandlers?.onChange(event, session)
      }
    )

    return () => subscription?.unsubscribe()
  }, [])

  // Auth methods
  const signIn = async (email, password) => {
    try {
      const { data, error } = await supabase?.auth?.signInWithPassword({ email, password })
      return { data, error }
    } catch (error) {
      return { error: { message: 'Network error. Please try again.' } }
    }
  }

  const signOut = async () => {
    try {
      const { error } = await supabase?.auth?.signOut()
      if (!error) {
        setUser(null)
        profileOperations?.clear()
      }
      return { error }
    } catch (error) {
      return { error: { message: 'Network error. Please try again.' } }
    }
  }

  const updateProfile = async (updates) => {
    if (!user) return { error: { message: 'No user logged in' } }
    
    try {
      const { data, error } = await supabase?.from('user_profiles')?.update(updates)?.eq('id', user?.id)?.select()?.single()
      if (!error) setUserProfile(data)
      return { data, error }
    } catch (error) {
      return { error: { message: 'Network error. Please try again.' } }
    }
  }

  const loadUserProfile = async (userId) => {
    try {
      const { data, error } = await supabase
        ?.from('user_profiles')
        ?.select('*')
        ?.eq('id', userId)
        ?.single();

      if (error) throw error;

      setUserProfile(data);
      
      // Cache user's timezone for consistent date handling
      if (data?.timezone) {
        cacheUserTimezone(data?.timezone);
      }
    } catch (error) {
      console.error('Error loading user profile:', error);
    } finally {
      setLoading(false);
    }
  };

  const value = {
    user,
    userProfile,
    loading,
    signIn,
    signOut,
    updateProfile,
    isAuthenticated: !!user
  }

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}
